import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingCart, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { MolecularBg } from "../components/MolecularBg";
import { PriceDisplay, SaleBadge, BnplNotice } from "../components/PriceDisplay";
import { ProductImage } from "../components/ProductImage";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

export const WishlistPage = () => {
    const { slugs, remove, ready, pendingMerge, mergeGuestNow, dismissMerge } = useWishlist();
    const { addItem } = useCart();
    const { user } = useAuth();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!ready) return;
        if (slugs.length === 0) {
            setProducts([]);
            setLoading(false);
            return;
        }
        (async () => {
            try {
                const { data } = await api.get("/products");
                const map = new Map(data.map((p) => [p.slug, p]));
                setProducts(slugs.map((s) => map.get(s)).filter(Boolean));
            } catch {
                toast.error("Failed to load wishlist");
            }
            setLoading(false);
        })();
    }, [slugs, ready]);

    return (
        <div className="pt-28 pb-20 min-h-screen relative" data-testid="wishlist-page">
            <MolecularBg intensity="low" />
            <div className="container-x relative z-10 max-w-5xl">
                <span className="eyebrow">// Saved For Later</span>
                <h1 className="font-display text-4xl md:text-5xl font-extrabold text-white tracking-tighter mt-2 mb-3 italic">
                    Your <span className="text-[var(--gold)]">Wishlist</span>
                </h1>
                <p className="text-sm text-[var(--text-secondary)] mb-8">
                    {products.length === 0
                        ? "No saved products yet. Tap the heart on any product to add it here."
                        : `${products.length} product${products.length === 1 ? "" : "s"} saved.`}
                </p>

                {user && pendingMerge && pendingMerge.length > 0 && (
                    <div
                        className="glass-elevated rounded-2xl p-4 mb-6 flex items-center gap-4 flex-wrap border border-[var(--gold)]/30 bg-[rgba(233,188,11,0.04)]"
                        data-testid="wishlist-merge-banner"
                    >
                        <Sparkles className="text-[var(--gold)]" size={20} />
                        <div className="flex-1 min-w-[200px]">
                            <div className="text-sm font-display font-bold text-white">Merge your guest wishlist?</div>
                            <div className="text-xs text-[var(--text-secondary)] mt-0.5">
                                We found {pendingMerge.length} item{pendingMerge.length === 1 ? "" : "s"} saved before you signed in.
                            </div>
                        </div>
                        <button
                            onClick={mergeGuestNow}
                            className="btn-solid"
                            data-testid="wishlist-merge-confirm"
                        >
                            Merge into account
                        </button>
                        <button
                            onClick={dismissMerge}
                            className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-white"
                            data-testid="wishlist-merge-dismiss"
                        >
                            Dismiss
                        </button>
                    </div>
                )}

                {loading ? (
                    <div className="py-12 text-center text-[var(--text-secondary)]">Loading…</div>
                ) : products.length === 0 ? (
                    <div className="glass rounded-2xl p-12 text-center" data-testid="wishlist-empty">
                        <Heart size={48} className="mx-auto text-[var(--text-muted)] mb-4" />
                        <p className="text-[var(--text-secondary)] mb-6">Your wishlist is empty.</p>
                        <Link to="/shop" className="btn-solid inline-block">Browse the catalog</Link>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {products.map((p) => {
                            const inStock = (p.stock ?? 0) > 0;
                            return (
                                <div
                                    key={p.slug}
                                    className="glass-elevated rounded-2xl p-4 flex items-center gap-4 flex-wrap"
                                    data-testid={`wishlist-row-${p.slug}`}
                                >
                                    <Link to={`/product/${p.slug}`} className="relative shrink-0 w-28">
                                        <ProductImage product={p} ratio="3 / 2" className="rounded-xl" />
                                        <div className="absolute -top-1.5 -left-1.5 z-10">
                                            <SaleBadge product={p} />
                                        </div>
                                    </Link>
                                    <div className="flex-1 min-w-[180px]">
                                        <Link to={`/product/${p.slug}`} className="font-display font-bold text-white hover:text-[var(--gold)]">
                                            {p.name}
                                        </Link>
                                        <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] mt-1">
                                            {p.dosage} · {inStock ? <span className="text-emerald-300">In stock</span> : <span className="text-red-300">Sold out</span>}
                                        </div>
                                        <div className="mt-2">
                                            <PriceDisplay product={p} size="sm" />
                                            <BnplNotice product={p} size="sm" className="mt-1" />
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => {
                                            const res = addItem(p, 1);
                                            if (res && res.ok === false) toast.error("Item is out of stock");
                                            else toast.success("Added to cart");
                                        }}
                                        disabled={!inStock}
                                        className="px-4 py-2 rounded-full bg-[var(--gold)] text-black text-xs font-mono uppercase tracking-widest font-bold hover:bg-[#F5C932] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                                        data-testid={`wishlist-add-to-cart-${p.slug}`}
                                    >
                                        <ShoppingCart size={12} /> Add to cart
                                    </button>
                                    <button
                                        onClick={() => remove(p.slug)}
                                        className="p-2 text-[var(--text-muted)] hover:text-red-400"
                                        aria-label={`Remove ${p.name} from wishlist`}
                                        data-testid={`wishlist-remove-${p.slug}`}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};
